import { useEffect, useMemo, useState } from 'react'
import { Download, Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'

function formatAmount(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

export default function Microsoft360LenovoRechargeTab() {
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState({ start_date: '2026-06-05', end_date: '', latest_date: '' })
  const [list, setList] = useState([])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/reporting-reports/ms-360-lenovo-recharge')
      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setRange({
          start_date: data.start_date || '2026-06-05',
          end_date: data.end_date || '',
          latest_date: data.latest_date || ''
        })
        setList(Array.isArray(data.list) ? data.list : [])
      }
    } catch (error) {
      console.error('获取特定渠道统计报表失败:', error)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const displayRows = useMemo(() => {
    return [...list].sort((a, b) => String(b.stat_date || '').localeCompare(String(a.stat_date || '')))
  }, [list])

  const handleExport = () => {
    if (displayRows.length === 0) {
      return
    }

    exportRowsToExcel({
      fileName: `特定渠道统计_${range.start_date || '2026-06-05'}_${range.end_date || range.latest_date || 'latest'}`,
      sheetName: '特定渠道统计',
      columns: [
        { header: '日期', key: 'stat_date', width: 14 },
        { header: '微软充值金额', value: (row) => formatAmount(row.microsoft_recharge_amount), width: 16 },
        { header: '360充值金额', value: (row) => formatAmount(row.ly360_recharge_amount), width: 16 },
        { header: '联想充值金额', value: (row) => formatAmount(row.lenovo_recharge_amount), width: 16 },
        { header: '腾讯充值金额', value: (row) => formatAmount(row.tencent_recharge_amount), width: 16 },
        { header: '百度充值金额', value: (row) => formatAmount(row.baidu_recharge_amount), width: 16 },
        { header: '系统之家充值金额', value: (row) => formatAmount(row.xitongzhijia_recharge_amount), width: 16 }
      ],
      rows: displayRows
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">特定渠道统计</h2>
          <p className="mt-1 text-sm text-gray-500">展示 2026-06-05 起微软、360、联想、腾讯、百度、系统之家六个渠道的每日充值金额</p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          刷新
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={loading || displayRows.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
        >
          <Download className="h-4 w-4" />
          导出 Excel
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        区间：{range.start_date || '--'} 至 {range.end_date || '--'}，最新统计日期：{range.latest_date || '--'}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">微软充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">360充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">联想充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">腾讯充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">百度充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">系统之家充值金额</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                displayRows.map((item) => (
                  <tr key={item.stat_date} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{item.stat_date}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.microsoft_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.ly360_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.lenovo_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.tencent_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.baidu_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.xitongzhijia_recharge_amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
